import { z } from 'zod';

/**
 * Profile Form Schema
 * Validates user profile information
 */
export const profileSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be less than 50 characters'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  phone: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^\+?[1-9]\d{1,14}$/.test(val),
      'Please enter a valid phone number'
    ),
  bio: z
    .string()
    .max(500, 'Bio must be less than 500 characters')
    .optional(),
  website: z
    .string()
    .url('Please enter a valid URL')
    .optional()
    .or(z.literal('')),
  dateOfBirth: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const date = new Date(val);
        const now = new Date();
        const age = now.getFullYear() - date.getFullYear();
        return age >= 13 && age <= 120;
      },
      'You must be at least 13 years old'
    ),
});

export type ProfileFormData = z.infer<typeof profileSchema>;

/**
 * Instagram Connection Schema
 * Validates Instagram handle format
 */
export const instagramConnectionSchema = z.object({
  instagramHandle: z
    .string()
    .min(1, 'Instagram handle is required')
    .regex(
      /^[a-zA-Z0-9._]{1,30}$/,
      'Instagram handle can only contain letters, numbers, periods, and underscores'
    ),
});

export type InstagramConnectionFormData = z.infer<typeof instagramConnectionSchema>;

/**
 * Social Links Schema
 * Validates social media profile URLs
 */
export const socialLinksSchema = z.object({
  instagram: z
    .string()
    .url('Please enter a valid Instagram URL')
    .regex(/instagram\.com/, 'Must be an Instagram URL')
    .optional()
    .or(z.literal('')),
  tiktok: z
    .string()
    .url('Please enter a valid TikTok URL')
    .regex(/tiktok\.com/, 'Must be a TikTok URL')
    .optional()
    .or(z.literal('')),
  youtube: z
    .string()
    .url('Please enter a valid YouTube URL')
    .regex(/youtube\.com|youtu\.be/, 'Must be a YouTube URL')
    .optional()
    .or(z.literal('')),
  twitter: z
    .string()
    .url('Please enter a valid Twitter/X URL')
    .regex(/twitter\.com|x\.com/, 'Must be a Twitter/X URL')
    .optional()
    .or(z.literal('')),
});

export type SocialLinksFormData = z.infer<typeof socialLinksSchema>;
